import { h, Component } from 'preact';
import { observer } from 'mobx-preact';

import PubSub, { topics } from '../../lib/pubsub';
import util from '../../lib/util';

import Top from '../navigation/top';
import Autocomplete from '../form/autocomplete';
import Images from '../form/images';
import { FormRow } from '../form/formField';

import CategoryTree from './tree';
import CategoryNav from './nav';

const initialState = {
    height: 100,
};

const FIELDS = {
    id: { label: 'Db #', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    articleno: { label: 'Articleno #', type: 'text', readonly: true, help: 'Field is imported from Visma: "ArticleNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    name: { label: 'Product name', type: 'text', readonly: true, help: 'Field is imported from Visma: "Name" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    nameDisplay: { label: 'Product webname', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrWebName" via new Visma SQL -> S3 -> Lambda -> API. File "consumerweb_products.csv"' },
    brand: { label: 'Brand', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrBRAND" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    vismaBrand: { label: 'Visma Brand', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrBRAND" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    brandno: { label: 'Brandno', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    itemCategory: { label: 'Item category', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrCategory" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    productGroup: { label: 'Product group', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrProductGroup" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    group: { label: 'Group', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    link: { label: 'Link to the dealerweb', type: 'text', readonly: true, help: 'Field is generated by the DealerWeb system.' },

    mainCategory: { label: 'Main category', type: 'text', readonly: true, aliasFor: 'maingroupname', help: 'Field is imported from Visma\'s maingroup table and related via: "MainGroupNo"' },
    intermediateCategory: { label: 'Intermediate category', type: 'text', readonly: true, aliasFor: 'intermediategroupname', help: 'Field is imported from Visma\'s intermediategroup table and related via: "IntermediateGroupNo"' },
    subCategory: { label: 'Sub category', type: 'text', readonly: true, aliasFor: 'subgroupname', help: 'Field is imported from Visma\'s subgroup table and related via: "SubGroupNo"' },

    maingroupname: { label: 'Maingroup name', type: 'text', readonly: true, help: 'Field is imported from Visma\'s maingroup table and related via: "MainGroupNo"' },
    maingroupno: { label: 'Maingroup #', type: 'text', readonly: true, help: 'Field is imported from Visma: "MainGroupNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    maingroupid: { label: 'Maingroup id', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    maingroupsort: { label: 'Maingroup sort', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },

    intermediategroupname: { label: 'Intermediategroup name', type: 'text', readonly: true, help: 'Field is imported from Visma\'s intermediategroup table and related via: "IntermediateGroupNo"' },
    intermediategroupno: { label: 'Intermediategroup #', type: 'text', readonly: true, help: 'Field is imported from Visma: "IntermediateGroupNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    intermediategroupid: { label: 'Intermediategroup id', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    intermediategroupsort: { label: 'Intermediategroup sort', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },

    subgroupname: { label: 'Subgroup name', type: 'text', readonly: true, help: 'Field is imported from Visma\'s subgroup table and related via: "SubGroupNo"' },
    subgroupno: { label: 'Subgroup #', type: 'text', readonly: true, help: 'Field is imported from Visma: "SubGroupNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    subgroupid: { label: 'Subgroup id', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    subgroupsort: { label: 'Subgroup sort', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },

    showonwebyesno: { label: 'Show on web', type: 'text', readonly: true, help: 'Field is imported from Visma: "ShowOnWebYesNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    showOnConsumerWeb: { label: 'Show on ConsumerWeb', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrConsumerWeb" via new Visma SQL -> S3 -> Lambda -> API. File "consumerweb_products.csv"' },
    is_new: { label: 'Is new', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrNewDW" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    isActive: { label: 'Is active', type: 'text', readonly: true, help: 'Field is imported from Visma: "InActiveYesNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },

    sort: { label: 'Sort', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    tradeItemNumber: { label: 'Trade item number', type: 'text', readonly: true, aliasFor: 'globalTradeItemNumber', help: 'Field is imported from Visma: "EANNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    color: { label: 'Color', type: 'text', readonly: true, help: 'Field is imported from Visma\'s color table and related via: "ZUsrFarge"' },
    size: { label: 'Size', type: 'text', readonly: true, help: 'Field is imported from Visma\'s size table and related via: "ZUsrStorrelse"' },
    weight: { label: 'Weight', type: 'text', readonly: true, help: 'Field is imported from Visma: "Weight" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },

    packingWeight: { label: 'Packing Weight', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrPackingWeight" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    volume: { label: 'Volume', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrVolume" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },

    length: { label: 'Length', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrLength" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    width: { label: 'Width', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrWidth" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    height: { label: 'Height', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrHeight" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },

    countryOfOrigin: { label: 'Country of origin', type: 'text', readonly: true, help: 'Field is imported from Visma: "CountryOfOriginNo" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    commodityCode: { label: 'Commodity code', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrCommodityCode" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    availability: { label: 'Availability', type: 'text', readonly: true, help: 'Field is calculated from "unitsInStock", "supplierOrderLines" and "customerOrderLines"' },
    introYear: { label: 'Intro Year', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrIntroYear" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    unitsInStock: { label: 'Units in stock', type: 'text', readonly: true, help: 'Field is imported from Visma: "unitsInStock" via new Visma SQL -> S3 -> Lambda -> API. File "unitinstock.csv"' },
    supplierOrderLines: { label: 'Supplier order lines', type: 'array', readonly: true, help: 'Field is imported from Visma: via new Visma SQL -> S3 -> Lambda -> API. File "supplierorderline.csv"' },
    customerOrderLines: { label: 'Customer order lines', type: 'array', readonly: true, help: 'Field is imported from Visma: via new Visma SQL -> S3 -> Lambda -> API. File "customerorderline.csv"' },

    priceNOK: { label: 'Price NOK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    priceSEK: { label: 'Price SEK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    priceDKK: { label: 'Price DKK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    priceGBP: { label: 'Price GBP', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    priceEUR: { label: 'Price EUR', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    priceCHF: { label: 'Price CHF', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    pricelist: { label: 'Pricelist', type: 'object', readonly: true, help: 'Field is imported from Visma: via new Visma SQL -> S3 -> Lambda -> API. File "PriceList.xml"' },
    pricelistCombo: { label: 'Pricelist combo', type: 'text', readonly: true, help: 'Field is DealerWeb only. Can be update via DealerWeb admin/multiple update.' },

    imgs: { label: 'Images', type: 'images', readonly: false, help: 'Field is DealerWeb only.' },

    description: { label: 'Description', type: 'textarea', readonly: false, rows: 5, help: 'Field is DealerWeb only.' },
    features: { label: 'Features', type: 'textarea', readonly: false, rows: 5, help: 'Field is DealerWeb only.' },
    tech: { label: 'Tech', type: 'textarea', readonly: false, rows: 5, help: 'Field is DealerWeb only.' },
    care: { label: 'Care', type: 'textarea', readonly: false, rows: 5, help: 'Field is DealerWeb only.' },
    howtouse: { label: 'Howto use', type: 'textarea', readonly: false, rows: 5, help: 'Field is DealerWeb only.' },

    descriptionHtml: { label: 'Description Html', type: 'textarea', readonly: true, rows: 5, help: 'Field is DealerWeb only.' },
    featuresHtml: { label: 'Features Html', type: 'textarea', readonly: true, rows: 5, help: 'Field is DealerWeb only.' },
    techHtml: { label: 'Tech Html', type: 'textarea', readonly: true, rows: 5, help: 'Field is DealerWeb only.' },
    careHtml: { label: 'Care Html', type: 'textarea', readonly: true, rows: 5, help: 'Field is DealerWeb only.' },
    howtouseHtml: { label: 'Howto use Html', type: 'textarea', readonly: true, rows: 5, help: 'Field is DealerWeb only.' },

    youtubeNOK: { label: 'Youtube NOK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtubeSEK: { label: 'Youtube SEK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtubeDKK: { label: 'Youtube DKK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtubeGBP: { label: 'Youtube GBP', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtubeEUR: { label: 'Youtube EUR', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtubeCHF: { label: 'Youtube CHF', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube2NOK: { label: 'Youtube 2 NOK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube2SEK: { label: 'Youtube 2 SEK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube2DKK: { label: 'Youtube 2 DKK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube2GBP: { label: 'Youtube 2 GBP', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube2EUR: { label: 'Youtube 2 EUR', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube2CHF: { label: 'Youtube 2 CHF', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube3NOK: { label: 'Youtube 3 NOK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube3SEK: { label: 'Youtube 3 SEK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube3DKK: { label: 'Youtube 3 DKK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube3GBP: { label: 'Youtube 3 GBP', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube3EUR: { label: 'Youtube 3 EUR', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    youtube3CHF: { label: 'Youtube 3 CHF', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },

    preSale: { label: 'PreSale', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrPSWeb" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    preSaleNew: { label: 'PreSaleNew', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrNewPS" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    preSalePopular: { label: 'PreSalePopular', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrFocus" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    preSaleDiscontinued: { label: 'PreSaleDiscontinued', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrPSDiscontinued" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    psPriceNOK: { label: 'PS Price NOK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    psPriceSEK: { label: 'PS Price SEK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    psPriceDKK: { label: 'PS Price DKK', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    psPriceGBP: { label: 'PS Price GBP', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    psPriceEUR: { label: 'PS Price EUR', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },
    psPriceCHF: { label: 'PS Price CHF', type: 'text', readonly: false, help: 'Field is DealerWeb only.' },

    retailCurrency: { label: 'Retail currency', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    retailPrice: { label: 'Same as priceNOK', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    linkConsumerWeb: { label: 'Link to the consumerweb', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },

    dealerCurrency: { label: 'Dealer Currency from dealer pricelist', type: 'text', readonly: true, help: 'Field is imported from Visma: via new Visma SQL -> S3 -> Lambda -> API. File "PriceList.xml" and mapped with dealer pricelistno.' },
    dealerPrice: { label: 'Dealer price dealer pricelist', type: 'text', readonly: true, help: 'Field is imported from Visma: via new Visma SQL -> S3 -> Lambda -> API. File "PriceList.xml" and mapped with dealer pricelistno.' },
    dealerEurPrice: { label: 'Price from EUR pricelist 5', type: 'text', readonly: true, help: 'Field is imported from Visma: via new Visma SQL -> S3 -> Lambda -> API. File "PriceList.xml".' },
    apiLinkToProductGroup: { label: 'API Link to product', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },
    apiLinkToProductMainCategory: { label: 'API Link to product group', type: 'text', readonly: true, help: 'Field is DealerWeb only.' },


    colorId: { label: 'Color ID', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrFarge" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    sizeId: { label: 'Size ID', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrStorrelse" via new Visma SQL -> S3 -> Lambda -> API. File "artikkel.csv"' },
    option_1: { label: 'Option 1', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrOptionI" via new Visma SQL -> S3 -> Lambda -> API. File "consumerweb_products.csv"' },
    option_1_name: { label: 'Option 1 name', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrOptionNameI" via new Visma SQL -> S3 -> Lambda -> API. File "consumerweb_products.csv"' },
    option_2: { label: 'Option 2', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrOptionII" via new Visma SQL -> S3 -> Lambda -> API. File "consumerweb_products.csv"' },
    option_2_name: { label: 'Option 2 name', type: 'text', readonly: true, help: 'Field is imported from Visma: "ZUsrOptionNameII" via new Visma SQL -> S3 -> Lambda -> API. File "consumerweb_products.csv"' },

    translations: { label: 'Translations', type: 'object', readonly: false, help: 'Google translations of the texts.' },
    ratings: { label: 'Ratings', type: 'object', readonly: true, help: 'Dealer ratings of the products.' },
    // subgroupsort: { label: 'Link', type: 'text', readonly: true, help: '' },
};

@observer
class ProductEdit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...initialState,
        };
        this.updateTimer = {};
        const { articleStore, categoryStore, productStore } = this.props.stores;
        // categoryStore.load(props.categoryid);
        // categoryStore.tree();
        productStore.load(encodeURIComponent(props.productid));
        this.subscriptions();

        this.fields = [
            {
                tabname: 'Basics',
                icon: 'fas fa-user-edit',
                fields: ['id', 'articleno', 'name', 'nameDisplay', 'brand', 'itemCategory', 'productGroup', 'group', 'link'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Category',
                icon: 'fas fa-folder-open',
                fields: ['vismaBrand', 'brandno', 'mainCategory', 'intermediateCategory', 'subCategory',
                    'maingroupname', 'maingroupno', 'maingroupsort',
                    'intermediategroupname', 'intermediategroupno', 'intermediategroupsort',
                    'subgroupname', 'subgroupno', 'subgroupsort'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Status',
                icon: 'fas fa-traffic-light',
                fields: ['showonwebyesno', 'showOnConsumerWeb', 'is_new', 'isActive'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Properties',
                icon: 'fas fa-cogs',
                fields: ['sort', 'tradeItemNumber', 'color', 'size', 'weight', 'packingWeight', 'volume', 'length', 'width', 'height', 'countryOfOrigin', 'commodityCode',
                    'availability', 'introYear', 'unitsInStock', 'supplierOrderLines', 'customerOrderLines'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Price',
                icon: 'fas fa-money-bill-alt',
                fields: ['priceNOK', 'priceSEK', 'priceDKK', 'priceGBP', 'priceEUR', 'priceCHF', 'pricelist', 'pricelistCombo'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Images',
                icon: 'fas fa-image',
                fields: [
                    {
                        name: 'imgs',
                        label: 'Images',
                        type: 'object',
                        view: (val, opt) => {
                            const opts = {
                                id: opt.id,
                                name: opt.name,
                                value: val,
                                updateField: opt.updateField,
                                handleAddImage: (imageObject) => {
                                    const options = opt;
                                    this.addProductImage(options.id, imageObject, options.object);
                                },
                                handleDelImage: (imageId) => {
                                    const options = opt;
                                    this.delProductImage(options.id, imageId, options.object);
                                },
                                object: opt.object,
                            };
                            return <Images {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                ],
            },
            {
                tabname: 'Files',
                icon: 'fas fa-image',
                fields: [
                    {
                        name: 'files',
                        label: 'Files',
                        type: 'object',
                        view: (val, opt) => {
                            const opts = {
                                id: opt.id,
                                name: opt.name,
                                value: val,
                                updateField: opt.updateField,
                                handleAddImage: (imageObject) => {
                                    const options = opt;
                                    this.addProductImage(options.id, imageObject, options.object);
                                },
                                handleDelImage: (imageId) => {
                                    const options = opt;
                                    this.delProductFile(options.id, imageId, options.object);
                                },
                                object: opt.object,
                            };
                            return <Images {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                ],
            },
            {
                tabname: 'Text',
                icon: 'fas fa-file-alt',
                fields: ['description', 'features', 'tech', 'care', 'howtouse',
                    'youtubeNOK', 'youtubeSEK', 'youtubeDKK', 'youtubeGBP', 'youtubeEUR', 'youtubeCHF',
                    'youtube2NOK', 'youtube2SEK', 'youtube2DKK', 'youtube2GBP', 'youtube2EUR', 'youtube2CHF',
                    'youtube3NOK', 'youtube3SEK', 'youtube3DKK', 'youtube3GBP', 'youtube3EUR', 'youtube3CHF',
                ].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Translations',
                icon: 'fas fa-file-alt',
                fields: ['translations'].map(f => util.makeField(f, FIELDS)),

            },
            {
                tabname: 'Ratings',
                icon: 'fas fa-file-alt',
                fields: ['ratings'].map(f => util.makeField(f, FIELDS)),

            },
            {
                tabname: 'Related',
                icon: 'fas fa-paperclip',
                fields: [
                    {
                        name: 'children',
                        label: 'Related combo articles (OUTFIT)',
                        type: 'object',
                        view: (val) => {
                            const opts = {
                                name: 'children',
                                placeholder: 'Search for product...',
                                value: val,
                                valueDisplayMap: v => <span>
                                    <span class='font-weight-light'>{v.articleno}:</span> {v.name}
                                </span>,
                                autocompleteList: productStore.searchResults,
                                oninput: (e) => {
                                    clearTimeout(this.searchTimer);
                                    this.searchTimer = setTimeout(async () => {
                                        productStore.search(e.target.value, { searchFields: 'name,articleno', skipTextIndex: 1, showall: 1 });
                                    }, 500);
                                },
                                addElement: (idx) => {
                                    productStore.addElement(idx, 'product', 'children');
                                    productStore.resetSearch();
                                },
                                removeElement: (id) => {
                                    productStore.removeElement(id, 'product', 'children');
                                    productStore.resetSearch();
                                },
                                handleKeydown: (e) => {
                                    if (e.key === 'ArrowDown') {
                                        if (productStore.totalSearch === 0) {
                                            productStore.search(e.target.value, { searchFields: 'name,articleno', skipTextIndex: 1, showall: 1 });
                                        }
                                        productStore.incSearchSelectIdx();
                                    } else if (e.key === 'ArrowUp') {
                                        productStore.decSearchSelectIdx();
                                    } else if (e.key === 'Enter') {
                                        productStore.addElement(0, 'product', 'children');
                                        productStore.resetSearch();
                                    } else if (e.key === 'Escape') {
                                        productStore.resetSearch();
                                    }
                                },
                                currentIdx: productStore.searchSelectIdx,
                                currentId: productStore.searchSelectId,
                                total: productStore.totalSearch,
                            };
                            return <Autocomplete {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                    {
                        name: 'categories',
                        label: 'Related categories',
                        type: 'object',
                        view: (val) => {
                            const opts = {
                                placeholder: 'Search for category...',
                                value: val,
                                valueDisplayMap: v => <span>
                                    <span class='font-weight-light'>{v.groupno}:</span> {v.name}
                                </span>,
                                autocompleteList: categoryStore.searchResults,
                                oninput: (e) => {
                                    clearTimeout(this.searchTimer);
                                    this.searchTimer = setTimeout(async () => {
                                        categoryStore.search(e.target.value, { searchFields: 'name', intSearchFields: 'groupno', skipTokenizer: 1 });
                                    }, 500);
                                },
                                addElement: (idx) => {
                                    productStore.addElement(categoryStore.getElement(idx), 'product', 'categories');
                                    categoryStore.resetSearch();
                                },
                                removeElement: (id) => {
                                    productStore.removeElement(id, 'product', 'categories');
                                    categoryStore.resetSearch();
                                },
                                handleKeydown: (e) => {
                                    if (e.key === 'ArrowDown') {
                                        if (categoryStore.totalSearch === 0) {
                                            categoryStore.search(e.target.value, { searchFields: 'name', intSearchFields: 'groupno', skipTokenizer: 1 });
                                        }
                                        categoryStore.incSearchSelectIdx();
                                    } else if (e.key === 'ArrowUp') {
                                        categoryStore.decSearchSelectIdx();
                                    } else if (e.key === 'Enter') {
                                        productStore.addElement(categoryStore.getElement(), 'product', 'categories');
                                        categoryStore.resetSearch();
                                    } else if (e.key === 'Escape') {
                                        categoryStore.resetSearch();
                                    }
                                },
                                currentIdx: categoryStore.searchSelectIdx,
                                currentId: categoryStore.searchSelectId,
                                total: categoryStore.totalSearch,
                            };
                            return <Autocomplete {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                    {
                        name: 'articles',
                        label: 'Related articles',
                        type: 'object',
                        view: (val) => {
                            const opts = {
                                placeholder: 'Search for article...',
                                value: val,
                                valueDisplayMap: v => <span>
                                    <span class='font-weight-light'>{v.id}:</span> {v.title}
                                </span>,
                                autocompleteList: articleStore.searchResults,
                                oninput: (e) => {
                                    clearTimeout(this.searchTimer);
                                    this.searchTimer = setTimeout(async () => {
                                        articleStore.search(e.target.value, { searchFields: 'title' });
                                    }, 500);
                                },
                                addElement: (idx) => {
                                    productStore.addElement(articleStore.getElement(idx), 'product', 'articles');
                                    articleStore.resetSearch();
                                },
                                removeElement: (id) => {
                                    productStore.removeElement(id, 'product', 'articles');
                                    articleStore.resetSearch();
                                },
                                handleKeydown: (e) => {
                                    if (e.key === 'ArrowDown') {
                                        if (articleStore.totalSearch === 0) {
                                            articleStore.search(e.target.value, { searchFields: 'title' });
                                        }
                                        articleStore.incSearchSelectIdx();
                                    } else if (e.key === 'ArrowUp') {
                                        articleStore.decSearchSelectIdx();
                                    } else if (e.key === 'Enter') {
                                        productStore.addElement(articleStore.getElement(), 'product', 'articles');
                                        articleStore.resetSearch();
                                    } else if (e.key === 'Escape') {
                                        articleStore.resetSearch();
                                    }
                                },
                                currentIdx: articleStore.searchSelectIdx,
                                currentId: articleStore.searchSelectId,
                                total: articleStore.totalSearch,
                            };
                            return <Autocomplete {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                    {
                        name: 'articlesTech',
                        label: 'Related tech articles',
                        type: 'object',
                        view: (val) => {
                            const opts = {
                                name: 'relatedArticlesTech',
                                placeholder: 'Search for tech article...',
                                value: val,
                                valueDisplayMap: v => <span>
                                    <span class='font-weight-light'>{v.id}:</span> {v.title}
                                </span>,
                                autocompleteList: articleStore.searchResults,
                                oninput: (e) => {
                                    clearTimeout(this.searchTimer);
                                    this.searchTimer = setTimeout(async () => {
                                        articleStore.search(e.target.value, { searchFields: 'title' });
                                    }, 500);
                                },
                                addElement: (idx) => {
                                    productStore.addElement(articleStore.getElement(idx), 'product', 'articlesTech');
                                    articleStore.resetSearch();
                                },
                                removeElement: (id) => {
                                    productStore.removeElement(id, 'product', 'articlesTech');
                                    articleStore.resetSearch();
                                },
                                handleKeydown: (e) => {
                                    if (e.key === 'ArrowDown') {
                                        if (articleStore.totalSearch === 0) {
                                            articleStore.search(e.target.value, { searchFields: 'title' });
                                        }
                                        articleStore.incSearchSelectIdx();
                                    } else if (e.key === 'ArrowUp') {
                                        articleStore.decSearchSelectIdx();
                                    } else if (e.key === 'Enter') {
                                        productStore.addElement(articleStore.getElement(), 'product', 'articlesTech');
                                        articleStore.resetSearch();
                                    } else if (e.key === 'Escape') {
                                        articleStore.resetSearch();
                                    }
                                },
                                currentIdx: articleStore.searchSelectIdx,
                                currentId: articleStore.searchSelectId,
                                total: articleStore.totalSearch,
                            };
                            return <Autocomplete {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                    {
                        name: 'products',
                        label: 'Related products',
                        type: 'object',
                        view: (val) => {
                            const opts = {
                                name: 'relatedProducts',
                                placeholder: 'Search for product...',
                                value: val,
                                valueDisplayMap: v => <span>
                                    <span class='font-weight-light'>{v.articleno}:</span> {v.name}
                                </span>,
                                autocompleteList: productStore.searchResults,
                                oninput: (e) => {
                                    clearTimeout(this.searchTimer);
                                    this.searchTimer = setTimeout(async () => {
                                        productStore.search(e.target.value, { searchFields: 'name,articleno', skipTextIndex: 1 });
                                    }, 500);
                                },
                                addElement: (idx) => {
                                    productStore.addElement(idx, 'product', 'products');
                                    productStore.resetSearch();
                                },
                                removeElement: (id) => {
                                    productStore.removeElement(id, 'product', 'products');
                                    productStore.resetSearch();
                                },
                                handleKeydown: (e) => {
                                    if (e.key === 'ArrowDown') {
                                        if (productStore.totalSearch === 0) {
                                            productStore.search(e.target.value, { searchFields: 'name,articleno', skipTextIndex: 1 });
                                        }
                                        productStore.incSearchSelectIdx();
                                    } else if (e.key === 'ArrowUp') {
                                        productStore.decSearchSelectIdx();
                                    } else if (e.key === 'Enter') {
                                        productStore.addElement(0, 'product', 'products');
                                        productStore.resetSearch();
                                    } else if (e.key === 'Escape') {
                                        productStore.resetSearch();
                                    }
                                },
                                currentIdx: productStore.searchSelectIdx,
                                currentId: productStore.searchSelectId,
                                total: articleStore.totalSearch,
                            };
                            return <Autocomplete {...opts} />;
                        },
                        readonly: false,
                        help: 'Field is DealerWeb only.',
                    },
                ],
            },
            {
                tabname: 'Presale',
                icon: 'fas fa-clock',
                fields: ['preSale', 'preSaleNew', 'preSalePopular', 'preSaleDiscontinued',
                    'psPriceNOK', 'psPriceSEK', 'psPriceDKK', 'psPriceGBP', 'psPriceEUR', 'psPriceCHF'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'API',
                icon: 'fas fa-satellite-dish',
                fields: ['articleno', 'maingroupno', 'tradeItemNumber', 'name',
                    'brand', 'itemCategory', 'productGroup', 'commodityCode',
                    'mainCategory', 'intermediateCategory', 'subCategory',
                    'maingroupid', 'intermediategroupid', 'subgroupid',
                    'maingroupno', 'intermediategroupno', 'subgroupno',
                    'color', 'size', 'nameDisplay', 'availability', 'weight',
                    'packingWeight', 'volume', 'length', 'width', 'height',
                    'retailCurrency', 'retailPrice', 'introYear',
                    'description', 'features', 'tech', 'care', 'howtouse', 'images',
                    'priceNOK', 'priceSEK', 'priceDKK', 'priceGBP', 'priceEUR', 'priceCHF',
                    'youtubeNOK', 'youtubeSEK', 'youtubeDKK', 'youtubeGBP', 'youtubeEUR', 'youtubeCHF',
                    'link', 'linkConsumerWeb', 'imageRef',
                    'descriptionHtml', 'featuresHtml', 'techHtml', 'careHtml', 'howtouseHtml',
                    'dealerCurrency', 'dealerPrice', 'dealerEurPrice',
                    'apiLinkToProductGroup', 'apiLinkToProductMainCategory', 'imageRefPrefixes', 'sort',
                    'is_new', 'preSaleNew', 'preSaleDiscontinued', 'pricelistCombo',
                    'categories', 'articles', 'articlesTech', 'products'].map(f => util.makeField(f, FIELDS)),
            },
            {
                tabname: 'Visma',
                icon: 'fas fa-heart',
                fields: ['articleno', 'name', 'maingroupno', 'intermediategroupno', 'subgroupno',
                    'tradeItemNumber', 'vismaBrand', 'colorId', 'sizeId',
                    'preSale', 'preSaleNew', 'is_new', 'preSalePopular', 'showonwebyesno', 'preSaleDiscontinued',
                    'isActive', 'countryOfOrigin', 'weight', 'packingWeight', 'volume', 'length', 'width', 'height',
                    'itemCategory', 'commodityCode', 'introYear',
                    'productGroup',

                    'nameDisplay', 'option_1', 'option_1_name', 'option_2', 'option_2_name', 'showOnConsumerWeb',

                    'pricelist',
                ].map(f => util.makeField(f, FIELDS)),
            },
        ];
    }

    subscriptions() {
        PubSub.subscribe(topics.LEFT_MENU_HEIGHT_UPDATED, (height) => {
            this.setState({ height });
        });
    }

    updateField = async (id, field, value, object) => {
        const { productStore } = this.props.stores;
        productStore.updateField(id, field, value);

        clearTimeout(this.updateTimer[field]);
        this.updateTimer[field] = setTimeout(async () => {
            this.doUpdateField(id, field, value, object);
        }, 500);
    }

    async doUpdateField(id, field, value, object = {}) {
        const { productStore } = this.props.stores;
        const { updateAll } = productStore;
        // await productStore.saveField(id, field, value);
        const filter = {
            id,
            maingroupid: object.maingroupid,
            intermediategroupid: object.intermediategroupid,
            nameDisplay: object.nameDisplay,
        };
        if (field === 'imgs') {
            filter.colorId = object.colorId;
        }
        if (updateAll && filter.maingroupid && filter.intermediategroupid && filter.nameDisplay) {
            await productStore.updateMany(filter, field, value);
        } else {
            await productStore.saveField(id, field, value);
        }
    }

    addProductImage = async (id, imageObject, object) => {
        const { productStore } = this.props.stores;
        if (imageObject.mimetype.match(/^image/)) {
            productStore.addImage(id, imageObject, object);
        } else {
            productStore.addImage(id, imageObject, object, 'files');
        }
    }

    delProductImage = async (id, imageId, object) => {
        const { productStore } = this.props.stores;
        productStore.delImage(id, imageId, object);
    }

    delProductFile = async (id, imageId, object) => {
        const { productStore } = this.props.stores;
        productStore.delImage(id, imageId, object, 'files');
    }

    render(props) {
        const { height } = this.state;
        const { productStore } = this.props.stores;
        const { product, saved, updateAll } = productStore;

        let tabIdx = this.fields.findIndex(e => e.tabname === props.tabname);
        if (tabIdx < 0) {
            tabIdx = 0;
        }

        return (
            <div>
                <Top {...props} />

                <div class='row'>
                    <div class='col-12'>
                        <CategoryNav {...this.props} />
                    </div>
                </div>
                <div class='row'>
                    <div class='col-2 fixed-top overflow-auto'
                        style={`margin-top: ${height + 60}px; height: calc(100% - ${height + 60}px);`}
                    >
                        <small>
                            <CategoryTree {...this.props} />
                        </small>
                        <div class='text-center mt-4 mb-2'>
                            <small class='text-muted'>
                                <i class='fas fa-ellipsis-h' />
                            </small>
                        </div>
                    </div>
                    <div class='col-10 offset-2'>
                        <div class='d-flex flex-row bd-highlight mb-3 mt-4'>
                            <div>
                                <a href='/products' class={`mr-2 btn btn-sm btn-warning`}>
                                    <i class={`fas fa-arrow-left mr-1`} />
                                    Back
                                </a>
                            </div>
                            <div>
                                <h4>Edit: {product.articleno}: {product.name}</h4>
                            </div>
                            <div class='flex-grow-1 text-right'>
                                <button class='btn btn-link'
                                    onClick={() => productStore.toggleUpdateAll()}
                                >
                                    <i class={`${updateAll ? 'fas fa-toggle-on text-success' : 'fas fa-toggle-off text-muted'}`} />
                                </button>
                                <small class={`text-${updateAll ? 'success' : 'muted'}`}>
                                    Update all products with same nameDisplay and in the same cateogry.
                                </small>
                            </div>
                        </div>
                        <ul class='nav nav-tabs mb-2'>
                            {this.fields.map(tab => (
                                <li class='nav-item'>
                                    <a class={`nav-link ${props.tabname === tab.tabname ? 'active' : ''}`}
                                        href={`/products/${encodeURIComponent(props.productid)}/${tab.tabname}`}
                                    >
                                        <i class={`${tab.icon} mr-2`} />
                                        {tab.tabname}
                                    </a>
                                </li>
                            ))}
                        </ul>

                        {this.fields[tabIdx].fields.map(field => <FormRow
                            field={field}
                            object={product}
                            saved={saved}
                            updateField={this.updateField}
                        />)}

                    </div>
                </div>
            </div>
        );
    }
}

export default ProductEdit;



// WEBPACK FOOTER //
// ./components/products/edit.js